/* eslint-disable prettier/prettier -- behoud de afgesproken witregels binnen conditionals. */
import {
  RepeatWrapping,
  LinearSRGBColorSpace,
  SRGBColorSpace,
} from 'three'
import { textureConfig } from './textureConfig'
import { isAbortError, loadImageTexture, throwIfAborted } from './assetRequests'

const baseURL = 'https://3dassets.royalbotania.com/materials'

export function disposeTextures(textures) {
  new Set(Object.values(textures || {})).forEach((texture) => {
    if (texture?.dispose) {

      texture.dispose()

    }
  })
}

function constructTextureURL(materialData, resolution, type) {
  return `${baseURL}/${materialData.code}/${resolution}/${materialData.code}-${type.toLowerCase()}-${textureConfig[resolution]}.png`
}

export const unloadTextures = (materialKey, materialsMap) => {
  const [key, value] =
    [...materialsMap.entries()].find(
      ([, value]) => value.material.code === materialKey,
    ) || []

  if (value) {
    Object.entries(value.textures).forEach(([textureKey, textureValue]) => {
      if (textureValue) {
        //console.log(`🗑️ Unloading texture: ${textureKey}`)
        textureValue.dispose()
      }
    })

    materialsMap.delete(key)
    //console.log(`🗑️ Removed material: ${materialKey} from materialsMap`)
  }
}

export async function loadTexturesFromJSON(materialData, resolution, onError, signal) {
  throwIfAborted(signal)

  const textureURLs = {
    colorMap: materialData.mapTypes?.baseColor
      ? constructTextureURL(materialData, resolution, 'basecolor')
      : null,
    normalMap: materialData.mapTypes?.normal
      ? constructTextureURL(materialData, resolution, 'normal')
      : null,
    roughnessMap: materialData.mapTypes?.roughness
      ? constructTextureURL(materialData, resolution, 'roughness')
      : null,
    opacityMap: materialData.mapTypes?.opacity
      ? constructTextureURL(materialData, resolution, 'opacity')
      : null,
    metalnessMap: materialData.mapTypes?.metalness
      ? constructTextureURL(materialData, resolution, 'metalness')
      : null,
  }

  // laad parallel, zodat een afgebroken batch ook zijn reeds geladen maps kan opruimen.
  const textures = await Promise.allSettled([
    loadTexture(textureURLs.colorMap, SRGBColorSpace, false, onError, signal),
    loadTexture(textureURLs.normalMap, LinearSRGBColorSpace, false, onError, signal),
    loadTexture(textureURLs.roughnessMap, LinearSRGBColorSpace, false, onError, signal),
    loadTexture(textureURLs.opacityMap, LinearSRGBColorSpace, false, onError, signal),
    loadTexture(textureURLs.metalnessMap, LinearSRGBColorSpace, false, onError, signal),
  ])

  const loadedTextures = {
    colorMap: textures[0].status === 'fulfilled' ? textures[0].value : null,
    normalMap: textures[1].status === 'fulfilled' ? textures[1].value : null,
    roughnessMap: textures[2].status === 'fulfilled' ? textures[2].value : null,
    opacityMap: textures[3].status === 'fulfilled' ? textures[3].value : null,
    metalnessMap: textures[4].status === 'fulfilled' ? textures[4].value : null,
  }

  const abortedTexture = textures.find(
    (result) => result.status === 'rejected' && isAbortError(result.reason),
  )

  if (signal?.aborted || abortedTexture) {

    disposeTextures(loadedTextures)

    throwIfAborted(signal)

    throw abortedTexture.reason

  }

  return loadedTextures
}

const loadTexture = async (url, colorSpace, flipY = false, onError, signal) => {
  throwIfAborted(signal)

  if (!url) {

    return null

  }

  let texture

  try {

    texture = await loadImageTexture(url, signal)

    throwIfAborted(signal)

    texture.wrapS = texture.wrapT = RepeatWrapping
    texture.colorSpace = colorSpace
    texture.flipY = flipY

    return texture

  } catch (error) {

    texture?.dispose()

    if (!signal?.aborted && !isAbortError(error)) {

      console.warn('Texture loading error:')
      onError?.(error)

    }

    throwIfAborted(signal)

    throw error

  }
}
